// ============================================================
// EstimatorMenu.js
// One "🧰 Estimators ▾" dropdown replacing the row of separate estimator
// buttons in NewDocument and EditDocument.
//
// Self-contained: it owns only its own open/close state and calls the handler
// props it is given. It never navigates, saves a draft, or touches document
// state — each page keeps its own handleAddFrom…Estimator functions unchanged.
//
// An estimator appears in the menu ONLY when its handler prop is supplied, so a
// page that doesn't offer one just omits the prop. (EditDocument has no
// chain-link entry point, so it passes no onChainLink.)
//
// Placement note: both pages render this inside the Line Items card, which is
// `position: relative; z-index: 20; overflow: visible` — so an absolutely
// positioned menu is not clipped. The menu uses z-index 100 to match the
// product-search dropdown that already lives in that same card.
// ============================================================

import React, { useState, useEffect, useRef } from 'react';

// Shared with the row's other buttons so the trigger sits flush with them.
const BUTTON_BASE = {
  padding: '10px 16px',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(3,4,94,0.12)',
  transition: 'box-shadow 0.15s, transform 0.15s',
};

export default function EstimatorMenu({ onChainLink, onVinyl, onWood, onAluminum, disabled = false }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Dot colors carry over each estimator's former button color, so the menu
  // reads the same way the four buttons did.
  const items = [
    { key: 'chainLink', icon: '🔧', label: 'Chain Link', color: '#0077B6', onSelect: onChainLink },
    { key: 'vinyl', icon: '🏳️', label: 'Vinyl', color: '#0F6E56', onSelect: onVinyl },
    { key: 'wood', icon: '🪵', label: 'Wood', color: '#8B5E3C', onSelect: onWood },
    { key: 'aluminum', icon: '🔩', label: 'Aluminum', color: '#8A94A6', onSelect: onAluminum },
  ].filter(i => typeof i.onSelect === 'function');

  // Outside-click and Escape close the menu. Listeners are attached only while
  // open and removed on close/unmount. Containment is tested with a ref rather
  // than a CSS class: a click ON a menu item is inside the container, so the
  // mousedown listener can't close the menu before the item's click fires.
  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (items.length === 0) return null;

  const handleSelect = (onSelect) => {
    setOpen(false);
    onSelect();
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', fontFamily: 'Manrope, sans-serif' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          ...BUTTON_BASE,
          background: open ? '#0077B6' : '#E6F1FB',
          border: '1px solid #0077B6',
          color: open ? '#FFFFFF' : '#0077B6',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={e => {
          if (disabled) return;
          e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        🧰 Estimators ▾
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            zIndex: 100,
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 200,
            background: '#FFFFFF',
            border: '1px solid #E3ECF5',
            borderRadius: 12,
            boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)',
            padding: 6,
            overflow: 'hidden',
          }}
        >
          {items.map(item => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onClick={() => handleSelect(item.onSelect)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                background: 'transparent',
                border: 'none',
                borderRadius: 8,
                padding: '9px 10px',
                fontSize: 13,
                fontWeight: 600,
                color: '#03045E',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#E6F1FB'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: item.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ flexShrink: 0 }}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
